<template>
  <v-card elevation="2" outlined shaped tile class="my-4 px-2">
    <div>
      <v-alert type="info" dense border="left" class="mt-4">
        Create Assessments and Packages first. Then map Assessment IDs into Packages below.
      </v-alert>
    </div>
    <!-- start of b2bassessment -->
    <v-card-title>
      1. Assessment
      <v-spacer />
      <v-btn color="accent" small @click="openDialog()">Add</v-btn>
    </v-card-title>
    <v-card-subtitle class="grey--text text--darken-1"> Manage available assessments. </v-card-subtitle>
    <v-data-table
      class="b2b-assessment-table"
      :headers="headers"
      :items="assessments"
      item-value="id"
      show-expand
      hide-default-footer
      expand-on-click
      single-expand
      @update:expanded="onExpanded"
    >
      <!-- Normal Row Content -->
      <template #[`item.icon`]="{ item }">
        <v-avatar size="40" class="image-border">
          <v-img :src="item.icon" alt="icon" />
        </v-avatar>
      </template>

      <template #[`item.backgroundUrl`]="{ item }">
        <v-img width="40" :src="item.backgroundUrl" alt="icon" />
      </template>

      <template #[`item.status`]="{ item }">
        <v-switch
          color="primary"
          v-model="item.status"
          true-value="active"
          false-value="inactive"
          @change="toggleStatus(item)"
        />
      </template>

      <!-- Expanded Row -->
      <template v-slot:expanded-item="{ item, headers }">
        <td :colspan="headers.length">
          <v-form :ref="'editForm-' + item.id" v-model="Form.valid" lazy-validation>
            <v-container fluid class="my-4">
              <v-card-title> Edit Assessment </v-card-title>
              <v-card-subtitle :style="{ color: 'red' }">
                *Changes in this section require clicking the "Save" button.
              </v-card-subtitle>
              <v-row justify="end" class="mt-6">
                <v-btn color="primary" small @click="onSave(item.id)">Save</v-btn>
              </v-row>
              <v-row>
                <!-- Title & Description -->
                <v-col cols="12" md="5">
                  <v-text-field
                    name="title-th"
                    v-model="Form.title['th']"
                    label="Title TH"
                    :rules="[requiredRule]"
                    dense
                    outlined
                  />
                  <v-text-field
                    name="title-en"
                    v-model="Form.title['en']"
                    label="Title EN"
                    :rules="[requiredRule]"
                    dense
                    outlined
                  />
                  <v-text-field
                    name="description-th"
                    v-model="Form.description['th']"
                    label="Description TH"
                    :rules="[requiredRule]"
                    dense
                    outlined
                  />
                  <v-text-field
                    name="description-en"
                    v-model="Form.description['en']"
                    label="Description EN"
                    :rules="[requiredRule]"
                    dense
                    outlined
                  />
                </v-col>

                <!-- Icon -->
                <v-col cols="12" md="3" class="d-flex flex-column align-center">
                  <v-img
                    :src="selectIconImageBase64 || item.icon"
                    alt="icon"
                    max-width="200"
                    class="image-border"
                    style="cursor: pointer"
                    @click="() => openImagePicker('icon', item)"
                  />
                  <small class="grey--text text--darken-1">Icon</small>
                  <small class="grey--text text--darken-1">PNG ≤ 1MB</small>
                </v-col>

                <!-- Background Image -->
                <v-col cols="12" md="4" class="d-flex flex-column align-center">
                  <v-img
                    :src="selectImageBase64 || item.backgroundUrl"
                    alt="background"
                    max-width="200"
                    class="image-border"
                    style="cursor: pointer"
                    @click="() => openImagePicker('image', item)"
                  />
                  <small class="grey--text text--darken-1">Cover Image</small>
                  <small class="grey--text text--darken-1">PNG/JPG ≤ 1MB</small>
                </v-col>
              </v-row>

              <b2b-assessment-page
                title="Landing Pages"
                type="landing"
                :items="item.landing"
                :assessmentId="item.id"
                @onAdd="onCreatePage"
                @onUpdate="onUpdatePage"
                @onDelete="onDeletePage"
              />
              <b2b-assessment-page
                title="Result Pages"
                type="result"
                :items="item.result"
                :assessmentId="item.id"
                @onAdd="onCreatePage"
                @onUpdate="onUpdatePage"
                @onDelete="onDeletePage"
              />
              <b2b-assessment-question
                title="Questions"
                :questions="item.questions"
                :assessmentId="item.id"
                @onAdd="onCreateQuestion"
                @onUpdate="onUpdateQuestion"
                @onDelete="onDeleteQuestion"
              />

              <b2b-assessment-condition
                :resultPages="item.result"
                :conditions="item.condition"
                :assessmentId="item.id"
                @onSave="onSaveCondition"
              />

              <!-- Footer Actions -->
              <v-row justify="center" class="mt-6">
                <v-btn text color="error" small @click="onDelete(item.id)">Delete Assessment</v-btn>
              </v-row>
            </v-container>
          </v-form>
        </td>
      </template>
    </v-data-table>

    <!-- Add B2B Assessment Modal -->
    <v-dialog v-model="dialog" max-width="700px">
      <v-card>
        <v-card-title>
          Add B2B Assessment
          <v-spacer />
          <v-btn icon @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-form ref="formRef" v-model="newForm.valid" lazy-validation>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  name="new-title-th"
                  v-model="newForm.title.th"
                  label="Title TH"
                  :rules="[requiredRule]"
                  dense
                  outlined
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  name="new-title-en"
                  v-model="newForm.title.en"
                  label="Title EN"
                  :rules="[requiredRule]"
                  dense
                  outlined
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  name="new-description-th"
                  v-model="newForm.description.th"
                  label="Description TH"
                  :rules="[requiredRule]"
                  dense
                  outlined
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  name="new-description-en"
                  v-model="newForm.description.en"
                  label="Description EN"
                  :rules="[requiredRule]"
                  dense
                  outlined
                />
              </v-col>

              <v-col cols="12" class="d-flex flex-column align-center mt-4">
                <v-img :src="selectNewIconBase64" max-width="200" class="image-border mb-2" />
                <v-btn small color="secondary" @click="() => openImagePicker('new-icon')">Upload Icon</v-btn>
                <v-input :value="selectNewIconBase64" :rules="[imageRequiredRule]" hide-details="auto" class="pa-0">
                  <template v-slot:default>
                    <div></div>
                  </template>
                </v-input>
              </v-col>
              <v-col cols="12" class="d-flex flex-column align-center mt-4">
                <v-img :src="selectNewImageBase64" max-width="200" class="image-border mb-2" />
                <v-btn small color="secondary" @click="() => openImagePicker('new-background')"
                  >Upload Background</v-btn
                >
                <v-input :value="selectNewImageBase64" :rules="[imageRequiredRule]" hide-details="auto" class="pa-0">
                  <template v-slot:default>
                    <div></div>
                  </template>
                </v-input>
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn color="primary" :disabled="isCreateDisabled" @click="onCreateAssessment"> Create </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <input type="file" ref="imageInput" style="display: none" accept="image/*" @change="onSelectFile" />
    <!-- end of b2bassessment -->
    <!-- start of b2bassessmentpackage -->
    <div>
      <v-container fluid grid-list-xs fill-height>
        <v-layout column warp>
          <template>
            <v-card-title>
              2. Packages
              <v-spacer />
              <AddOrEditB2BAssessmentPackage
                :isAdd="true"
                @success="fetchB2BAssessmentPackageData"
                :corporateList="corporateList"
              />
            </v-card-title>
            <v-card-subtitle class="grey--text text--darken-1">
              Create packages to group multiple assessments.
            </v-card-subtitle>
            <v-data-table
              :headers="b2bAssessmentPackageHeaders"
              :items="b2bAssessmentPackages"
              :loading="b2bAssessmentPackageLoading"
              item-key="id"
              class="elevation-1"
            >
              <v-progress-linear v-slot:progress color="blue" indeterminate />
              <template v-slot:body="{ items }">
                <tbody>
                  <tr v-for="item in items" :key="item.id">
                    <td class="text-center">{{ item.id }}</td>
                    <td class="text-center">{{ item.organization_id }}</td>
                    <td class="text-center">{{ item.organization_name }}</td>
                    <td class="text-center">{{ item.package_type }}</td>
                    <td class="text-center">{{ item.name }}</td>
                    <td class="text-center">{{ item.description }}</td>
                    <td class="text-center">{{ item.duration_minute }}</td>
                    <td class="text-center">{{ item.start_at_display }}</td>
                    <td class="text-center">{{ item.end_at_display }}</td>
                    <td class="px-0 py-1 text-center">
                      <AddOrEditB2BAssessmentPackage
                        :isAdd="false"
                        @success="fetchB2BAssessmentPackageData"
                        :corporateList="corporateList"
                        :b2bAssessmentPackage="item"
                      />
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-data-table>
          </template>
        </v-layout>
      </v-container>
    </div>
    <!-- end of b2bassessmentpackage -->

    <!-- start of b2bassessmentpackageassignment -->
    <div>
      <v-container fluid grid-list-xs fill-height>
        <v-layout column warp>
          <template>
            <v-card-title>
              3. Assignment Mapping
              <v-spacer />
              <AddOrEditB2BAssessmentPackageAssignment
                :isAdd="true"
                @success="fetchB2BAssessmentPackageAssignmentData"
              />
            </v-card-title>
            <v-card-subtitle class="grey--text text--darken-1">
              Assign assessments to packages by selecting the desired Assessment ID.
            </v-card-subtitle>
            <v-data-table
              :headers="b2bAssessmentPackageAssignmentHeaders"
              :items="b2bAssessmentPackageAssignments"
              :loading="b2bAssessmentPackageAssignmentLoading"
              item-key="id"
              class="elevation-1"
            >
              <v-progress-linear v-slot:progress color="blue" indeterminate />
              <template v-slot:body="{ items }">
                <tbody>
                  <tr v-for="item in items" :key="item.id">
                    <td class="text-center">{{ item.id }}</td>
                    <td class="text-center">{{ item.package_id }}</td>
                    <td class="text-center">{{ item.b2b_assessment_id }}</td>
                    <td class="text-center">{{ item.type }}</td>
                    <td class="text-center">{{ item.created_at }}</td>
                    <td class="text-center">{{ item.updated_at }}</td>
                    <td class="px-0 py-1 text-center">
                      <AddOrEditB2BAssessmentPackageAssignment
                        :isAdd="false"
                        @success="fetchB2BAssessmentPackageAssignmentData"
                        :b2bAssessmentPackageAssignment="item"
                      />
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-data-table>
          </template>
        </v-layout>
      </v-container>
    </div>
    <!-- end of b2bassessmentpackageassignment -->
  </v-card>
</template>

<script>
import Form from '../../../form/form';
import { srcToBase64 } from '../../../utils/stringUtil';
import { Vue } from 'vue-property-decorator';
import B2bAssessmentPage from './B2bAssessmentPage.vue';
import B2bAssessmentQuestion from './B2bAssessmentQuestion.vue';
import B2bAssessmentCondition from './B2bAssessmentCondition.vue';
import B2BAssessmentService from '../../../services/b2bassessment';
import B2BAssessmentPackageService from '../../../services/b2bassessmentpackage';
import AddOrEditB2BAssessmentPackage from '../../../components/AddOrEditB2BAssessmentPackage.vue';
import B2BAssessmentPackageAssignmentService from '../../../services/b2bassessmentpackageassignment';
import AddOrEditB2BAssessmentPackageAssignment from '../../../components/AddOrEditB2BAssessmentPackageAssignment.vue';
import moment from 'moment-timezone';
import corporateService from '../../../services/corporate';
export default Vue.extend({
  components: {
    B2bAssessmentPage,
    B2bAssessmentQuestion,
    B2bAssessmentCondition,
    AddOrEditB2BAssessmentPackage,
    AddOrEditB2BAssessmentPackageAssignment,
  },
  props: {
    assessments: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      // start of b2bassessment data()
      dialog: false,
      Form: new Form({
        data() {
          return {
            title: { th: '', en: '' },
            description: { th: '', en: '' },
            icon: '',
            backgroundUrl: '',
          };
        },
      }),
      newForm: new Form({
        data() {
          return {
            title: { th: '', en: '' },
            description: { th: '', en: '' },
            icon: '',
          };
        },
      }),
      requiredRule: (v) => (v && v.toString().trim().length > 0) || 'Required',
      imageRequiredRule: (v) => !!v || 'Required',
      headers: [
        { text: 'ID', value: 'id', align: 'center' },
        { text: 'Type', value: 'code', align: 'center' },
        { text: 'Icon', value: 'icon', align: 'center' },
        { text: 'Title TH', value: 'title[th]' },
        { text: 'Description TH', value: 'description[th]' },
        { text: 'Result Count', value: 'result.length', align: 'center' },
        { text: 'Questions Count', value: 'questions.length', align: 'center' },
        { text: 'Background', value: 'backgroundUrl', align: 'center' },
        { text: 'Status', value: 'status', align: 'center' },
        { text: '', value: 'data-table-expand' },
      ],
      currentImageType: null,
      selectIconImageFileName: null,
      selectIconImageBase64: null,
      selectImageFileName: null,
      selectImageBase64: null,
      selectNewIconBase64: null,
      selectNewIconFileName: null,
      selectNewImageBase64: null,
      selectNewImageFileName: null,
      // end of b2bassessment data()
      // start of b2bassessmentpackage data()
      b2bAssessmentPackageLoading: false,
      b2bAssessmentPackageHeaders: [
        { text: 'ID', align: 'center', value: 'id', sortable: false },
        { text: 'Organization ID', align: 'center', value: 'organization_id', sortable: false },
        { text: 'Organization Name', align: 'center', value: 'organization_name', sortable: false },
        { text: 'Package Type', align: 'center', value: 'package_type', sortable: false },
        { text: 'Name', align: 'center', value: 'name', sortable: false },
        { text: 'Description', align: 'center', value: 'description', sortable: false },
        { text: 'Duration (min)', align: 'center', value: 'duration_minute', sortable: false },
        { text: 'Start At', align: 'center', value: 'start_at', sortable: false },
        { text: 'End At', align: 'center', value: 'end_at', sortable: false },
        { text: 'Action', align: 'center', value: 'action', sortable: false },
      ],
      b2bAssessmentPackages: [],
      corporateList: [],
      // end of b2bassessmentpackage data()

      // start of b2bassessmentpackageassignment data()
      b2bAssessmentPackageAssignmentLoading: false,
      b2bAssessmentPackageAssignmentHeaders: [
        { text: 'ID', align: 'center', value: 'id', sortable: false },
        { text: 'Package ID', align: 'center', value: 'package_id', sortable: false },
        { text: 'Assessment ID', align: 'center', value: 'b2b_assessment_id', sortable: false },
        { text: 'Type', align: 'center', value: 'type', sortable: false },
        { text: 'Created At', align: 'center', value: 'created_at', sortable: false },
        { text: 'Updated At', align: 'center', value: 'updated_at', sortable: false },
        { text: 'Action', align: 'center', value: 'action', sortable: false },
      ],
      b2bAssessmentPackageAssignments: [],
      // end of b2bassessmentpackageassignment data()
    };
  },
  methods: {
    // start of b2bassessment method{}
    openDialog() {
      this.newForm = {
        title: { th: '', en: '' },
        description: { th: '', en: '' },
        icon: '',
        preview: null,
        valid: false,
      };
      this.dialog = true;
      this.$nextTick(() => {
        if (this.$refs.formRef) this.$refs.formRef.resetValidation();
      });
    },
    async toggleStatus(item) {
      await B2BAssessmentService.update(item.id, { status: item.status });
      this.$emit('onFetch');
    },
    async onSaveCondition(assessmentId, conditions) {
      await B2BAssessmentService.update(assessmentId, { condition: conditions });
      this.$emit('onFetch');
    },
    async onCreateAssessment() {
      const isValid = this.$refs.formRef.validate();

      if (!isValid) {
        return; // stop here if form invalid
      }

      const body = {
        title: this.newForm.title,
        description: this.newForm.description,
        icon: this.selectNewIconBase64
          ? {
              fileName: this.selectNewIconFileName,
              base64: srcToBase64(this.selectNewIconBase64),
            }
          : '',
        backgroundUrl: this.selectNewImageBase64
          ? {
              fileName: this.selectNewImageFileName,
              base64: srcToBase64(this.selectNewImageBase64),
            }
          : '',
      };

      await B2BAssessmentService.create(body);
      this.$emit('onFetch');
      this.dialog = false;
    },
    onExpanded(expandedItem) {
      if (expandedItem.length > 0) {
        const item = expandedItem[0];
        if (item) this.loadItem(item);
      }
    },
    loadItem(item) {
      this.Form.title = JSON.parse(JSON.stringify(item.title));
      this.Form.description = JSON.parse(JSON.stringify(item.description));
      this.Form.icon = item.icon;
      this.Form.backgroundUrl = item.backgroundUrl;
      this.selectIconImageFileName = null;
      this.selectIconImageBase64 = null;
      this.selectImageFileName = null;
      this.selectImageBase64 = null;
      this.selectNewImageBase64 = null;
      this.selectNewImageFileName = null;
    },
    openImagePicker(type) {
      this.currentImageType = type;
      this.$refs.imageInput.click();
    },
    onSelectFile(e) {
      const files = e.target.files;
      const file = files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const base64String = event.target.result;

          if (this.currentImageType === 'icon') {
            this.selectIconImageFileName = file.name;
            this.selectIconImageBase64 = base64String;
          } else if (this.currentImageType === 'image') {
            this.selectImageFileName = file.name;
            this.selectImageBase64 = base64String;
          } else if (this.currentImageType === 'new-icon') {
            this.selectNewIconFileName = file.name;
            this.selectNewIconBase64 = base64String;
          } else if (this.currentImageType === 'new-background') {
            this.selectNewImageFileName = file.name;
            this.selectNewImageBase64 = base64String;
          }
        };
        reader.onerror = (error) => {
          console.error('Error reading file:', error);
        };
        reader.readAsDataURL(file);
      } else {
        console.error('No file selected');
      }
    },
    async onCreateQuestion(body) {
      await B2BAssessmentService.createQuestion(body);
      this.$emit('onFetch');
    },
    async onUpdateQuestion(id, body) {
      console.log(id, body);
      await B2BAssessmentService.updateQuestion(id, body);
      this.$emit('onFetch');
    },
    async onDeleteQuestion(id) {
      await B2BAssessmentService.deleteQuestion(id);
      this.$emit('onFetch');
    },
    async onCreatePage(body) {
      await B2BAssessmentService.createPage(body);
      this.$emit('onFetch');
    },
    async onUpdatePage(id, body) {
      await B2BAssessmentService.updatePage(id, body);
      this.$emit('onFetch');
    },
    async onDeletePage(id) {
      await B2BAssessmentService.deletePage(id);
      this.$emit('onFetch');
    },
    async onSave(id) {
      const formRef = this.$refs['editForm-' + id];

      if (!formRef) return;

      const isValid = formRef.validate();

      if (!isValid) {
        return; // stop if invalid
      }

      const body = {
        title: this.Form.title,
        description: this.Form.description,
      };

      if (this.selectImageBase64) {
        body.backgroundUrl = {
          fileName: this.selectImageFileName,
          base64: srcToBase64(this.selectImageBase64),
        };
      }

      if (this.selectIconImageBase64) {
        body.icon = {
          fileName: this.selectIconImageFileName,
          base64: srcToBase64(this.selectIconImageBase64),
        };
      }

      await B2BAssessmentService.update(id, body);
      this.$emit('onFetch');
    },

    async onDelete(id) {
      const confirm = await this.$root.$confirm(
        'Delete B2B Assessment',
        `Are you sure you want to delete this B2B assessment? This action cannot be undone.`,
        { color: 'red' }
      );

      if (!confirm) return;

      try {
        await B2BAssessmentService.delete(id);

        this.$emit('onFetch');

        this.$notify({
          title: 'Success',
          text: 'B2B assessment deleted successfully.',
          type: 'success',
        });
      } catch (error) {
        console.error('Error deleting B2B assessment:', error);

        this.$notify({
          title: 'Error',
          text: 'There was an error deleting the B2B assessment. Please try again.',
          type: 'error',
        });
      }
    },
    // end b2bassessment method{}
    // start of b2bassessmentpackage method{}
    async fetchB2BAssessmentPackageData() {
      this.b2bAssessmentPackageLoading = true;
      try {
        const data = await B2BAssessmentPackageService.fetchB2BAssessmentPackage('both');
        const userTz = this.getUserTimezone();

        this.b2bAssessmentPackages = data.map((item) => ({
          ...item,
          start_at_display: moment.utc(item.start_at).tz(userTz).format('DD MMM YYYY HH:mm'),
          end_at_display: moment.utc(item.end_at).tz(userTz).format('DD MMM YYYY HH:mm'),
        }));
      } finally {
        this.b2bAssessmentPackageLoading = false;
      }
    },

    getCorporateList() {
      this.b2bAssessmentPackageLoading = true;
      return new Promise((resolve, reject) => {
        let query = {};
        query.per_page = 1000;
        query.order = 'id';
        query.sort = 'asc';

        corporateService
          .all(query)
          .then((data) => {
            this.b2bAssessmentPackageLoading = false;
            resolve(data);
          })
          .catch((err) => {
            this.b2bAssessmentPackageLoading = false;
            reject(err);
          });
      });
    },

    getUserTimezone() {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    },

    formatToUserTimezone(datetime) {
      if (!datetime) return '';

      const userTz = this.getUserTimezone();

      return moment
        .utc(datetime) // DB value is UTC
        .tz(userTz) // convert to user's timezone
        .format('DD MMM YYYY HH:mm'); // no seconds
    },
    // end of b2bassessmentpackage method{}

    // start of b2bassessmentpackageassignment method{}
    async fetchB2BAssessmentPackageAssignmentData() {
      this.b2bAssessmentPackageAssignmentLoading = true;
      try {
        const data = await B2BAssessmentPackageAssignmentService.fetchB2BAssessmentPackageAssignment('both');
        const userTz = this.getUserTimezone();

        this.b2bAssessmentPackageAssignments = data.map((item) => ({
          ...item,
          created_at: moment.utc(item.created_at).tz(userTz).format('DD MMM YYYY HH:mm'),
          updated_at: moment.utc(item.updated_at).tz(userTz).format('DD MMM YYYY HH:mm'),
        }));
      } finally {
        this.b2bAssessmentPackageAssignmentLoading = false;
      }
    },

    getUserTimezone() {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    },

    formatToUserTimezone(datetime) {
      if (!datetime) return '';

      const userTz = this.getUserTimezone();

      return moment
        .utc(datetime) // DB value is UTC
        .tz(userTz) // convert to user's timezone
        .format('DD MMM YYYY HH:mm'); // no seconds
    },
    // end of b2bassessmentpackageassignment method{}
  },

  mounted() {
    // start of b2bassessmentpackage mounted()
    this.fetchB2BAssessmentPackageData();
    this.getCorporateList().then((data) => {
      this.corporateList = data.data;
    });
    // end of b2bassessmentpackage mounted()

    // start of b2bassessmentpackageassignment mounted()
    this.fetchB2BAssessmentPackageAssignmentData();
    // end of b2bassessmentpackageassignment mounted()
  },
  computed: {
    // start of b2bassessment computed()
    isCreateDisabled() {
      const titleTh = (this.newForm.title?.th || '').trim();
      const titleEn = (this.newForm.title?.en || '').trim();
      const descTh = (this.newForm.description?.th || '').trim();
      const descEn = (this.newForm.description?.en || '').trim();

      return !titleTh || !titleEn || !descTh || !descEn || !this.selectNewIconBase64 || !this.selectNewImageBase64;
    },
    // start of b2bassessment computed()
  },
});
</script>

<style scoped>
.image-border {
  border: 1px dashed grey;
}
</style>
